import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, X } from "lucide-react";

// Realistic Malay / Chinese / Indian first names for MY & SG vibe
const NAMES = [
    "Amir",
    "Daniel",
    "Hafiz",
    "Wei Jie",
    "Aisyah",
    "Faris",
    "Marcus",
    "Aman",
    "Zara",
    "Iqbal",
    "Jia Hui",
    "Rizwan",
    "Nadia",
    "Kai",
    "Syafiq",
    "Shafiq",
    "Mei Ling",
    "Aaron",
    "Lina",
    "Hakim",
    "Priya",
    "Joshua",
];

const LOCATIONS = [
    "Kuala Lumpur",
    "Singapore",
    "Penang",
    "Johor Bahru",
    "Ipoh",
    "Shah Alam",
    "Petaling Jaya",
    "Kuching",
    "Kota Kinabalu",
    "Singapore",
    "Singapore",
    "Selangor",
    "Malacca",
    "Putrajaya",
];

const ACTIONS = [
    "just joined MZ Trading Lab",
    "just accessed MZ Trading Lab",
    "joined MZ Trading Lab",
    "started learning with MZ Trading Lab",
];

const TIMES = [
    "Just now",
    "1 minute ago",
    "2 minutes ago",
    "5 minutes ago",
    "7 minutes ago",
    "10 minutes ago",
];

const buildMessage = () => {
    const name = NAMES[Math.floor(Math.random() * NAMES.length)];
    const city = LOCATIONS[Math.floor(Math.random() * LOCATIONS.length)];
    const action = ACTIONS[Math.floor(Math.random() * ACTIONS.length)];
    const time = TIMES[Math.floor(Math.random() * TIMES.length)];
    // Vary the message format
    const includeCity = Math.random() > 0.25;
    return {
        id: `${name}-${Date.now()}-${Math.random()}`,
        name: includeCity ? `${name} from ${city}` : name,
        action,
        time,
    };
};

const NotificationPopup = () => {
    const [visible, setVisible] = useState(false);
    const [closed, setClosed] = useState(false);
    const [message, setMessage] = useState(() => buildMessage());

    useEffect(() => {
        if (closed) return undefined;
        // First show after a short delay
        const firstShow = setTimeout(() => {
            setMessage(buildMessage());
            setVisible(true);
        }, 3500);

        return () => clearTimeout(firstShow);
    }, [closed]);

    useEffect(() => {
        if (closed) return undefined;
        // Cycle: show 5s, hide 4s, repeat
        if (!visible) return undefined;
        const hideTimer = setTimeout(() => setVisible(false), 5000);
        return () => clearTimeout(hideTimer);
    }, [visible, closed]);

    useEffect(() => {
        if (closed) return undefined;
        if (visible) return undefined;
        // After hidden, wait then show again with new message
        const nextTimer = setTimeout(() => {
            setMessage(buildMessage());
            setVisible(true);
        }, 4500);
        return () => clearTimeout(nextTimer);
    }, [visible, closed]);

    const handleClose = () => {
        setVisible(false);
        setClosed(true);
    };

    const initials = useMemo(() => {
        const first = message.name.split(" ")[0] || "M";
        return first.slice(0, 1).toUpperCase();
    }, [message]);

    return (
        <div
            aria-live="polite"
            className="fixed left-3 sm:left-5 z-[60] pointer-events-none"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}
        >
            <AnimatePresence>
                {visible && (
                    <motion.div
                        key={message.id}
                        data-testid="social-proof-popup"
                        initial={{ opacity: 0, x: -40, y: 20 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        exit={{ opacity: 0, x: -30, y: 10, scale: 0.95 }}
                        transition={{
                            duration: 0.45,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="pointer-events-auto w-[19rem] sm:w-[22rem] bg-white rounded-2xl shadow-2xl border border-gray-200/70 p-4 flex items-start gap-3.5"
                        role="status"
                    >
                        <div className="relative shrink-0">
                            <div className="h-12 w-12 rounded-full bg-[#0077b6] text-white flex items-center justify-center shadow-md shadow-[#0077b6]/30">
                                <Users className="h-6 w-6" />
                            </div>
                            <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 ring-2 ring-white" />
                        </div>

                        <div className="flex-1 min-w-0 pr-5">
                            <div
                                data-testid="popup-name"
                                className="font-display text-[15px] sm:text-base font-bold text-black leading-tight truncate"
                            >
                                {message.name}
                            </div>
                            <p
                                data-testid="popup-action"
                                className="mt-1 text-[13px] sm:text-sm text-gray-600 leading-snug"
                            >
                                {message.action}
                            </p>
                            <p className="mt-1.5 text-[11px] text-gray-400">
                                {message.time}
                            </p>
                        </div>

                        <button
                            type="button"
                            data-testid="popup-close-button"
                            aria-label="Close notification"
                            onClick={handleClose}
                            className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full flex items-center justify-center text-gray-400 hover:text-black hover:bg-gray-100 transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <span className="sr-only">{initials}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationPopup;
